import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Fade,
  GlobalStyles,
  Paper,
  Popper,
  Portal,
  Typography,
  Unstable_TrapFocus as FocusTrap,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AccessTime,
  ArrowBack,
  ArrowForward,
  DeleteOutline,
  Edit,
  Group as GroupIcon,
  PlaylistAdd,
  RecordVoiceOver,
  WavingHand,
} from "@mui/icons-material";

interface TranscriptTutorialDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Fires with the current step's `reveal` (or undefined) so the page can react
   * — preview the time controls, open the speakers/history dialog, etc.
   */
  onRevealChange?: (reveal: TutorialReveal | undefined) => void;
}

interface Step {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  /** `data-tour` value of the element to spotlight; omit for a centered card. */
  target?: string;
  /**
   * Make the described feature actually visible while this step is shown:
   * - `sentence-actions` / `add-divider`: force-reveal hover-only controls (CSS).
   * - `time-controls`: the page shows the range-setting panel in preview.
   * - `speakers-dialog` / `history-dialog`: the page opens that dialog.
   */
  reveal?:
    | "sentence-actions"
    | "add-divider"
    | "time-controls"
    | "speakers-dialog"
    | "history-dialog";
}

export type TutorialReveal = NonNullable<Step["reveal"]>;

const STEPS: Step[] = [
  {
    icon: <WavingHand />,
    title: "Welcome to the Transcript Editor",
    body: (
      <>
        The transcript follows the video as it plays, making it easy to review
        and edit. You can reopen this guide anytime from the{" "}
        <strong>Help</strong> button.
      </>
    ),
  },
  {
    icon: <GroupIcon />,
    title: "Manage speakers",
    body: (
      <>
        <Box component="span" sx={{ display: "block" }}>
          Use <strong>Manage Speakers</strong> to add, rename, or remove
          speakers.
        </Box>
        <Box component="span" sx={{ display: "block", mt: 1 }}>
          Rename labels like Speaker 1 to the person’s actual name. The new name
          updates everywhere in the transcript.
        </Box>
      </>
    ),
    target: "manage-speakers",
    reveal: "speakers-dialog",
  },
  {
    icon: <PlaylistAdd />,
    title: "Add a transcript entry",
    body: (
      <>
        <Box component="span" sx={{ display: "block" }}>
          If something is missing from the transcript, click{" "}
          <strong>Add Transcript Entry</strong> or hover between entries and
          select <strong>Add Entry</strong>.
        </Box>
        <Box component="span" sx={{ display: "block", mt: 1 }}>
          Choose the speaker, enter what was said, then set the start and end
          times so the entry is linked to the correct portion of the video. You
          can use <strong>Set to Playhead</strong> or enter the time manually.
        </Box>
      </>
    ),
    target: "add-divider",
    reveal: "add-divider",
  },
  {
    icon: <AccessTime />,
    title: "Set timestamps",
    body: (
      <>
        <Box component="span" sx={{ display: "block" }}>
          Every transcript entry must have a start and end time.
        </Box>
        <Box component="span" sx={{ display: "block", mt: 1 }}>
          Play or scrub the video to the correct location, then click{" "}
          <strong>Set to Playhead</strong> for the start and end times. For finer
          adjustments, use the fine controls or enter the time manually.
        </Box>
      </>
    ),
    target: "time-controls",
    reveal: "time-controls",
  },
  {
    icon: <Edit />,
    title: "Edit transcript text",
    body: (
      <>
        <Box component="span" sx={{ display: "block" }}>
          Click any transcript entry to edit what was said.
        </Box>
        <Box component="span" sx={{ display: "block", mt: 1 }}>
          Unsaved changes remain underlined until you save.
        </Box>
      </>
    ),
    target: "entry",
  },
  {
    icon: <DeleteOutline />,
    title: "Delete transcript text",
    body: (
      <>
        <Box component="span" sx={{ display: "block" }}>
          Hover over a sentence and click the <strong>Trash</strong> icon to
          remove it.
        </Box>
        <Box component="span" sx={{ display: "block", mt: 1 }}>
          If you remove every sentence in an entry, the entire entry is deleted.
        </Box>
      </>
    ),
    target: "entry",
    reveal: "sentence-actions",
  },
  {
    icon: <RecordVoiceOver />,
    title: "Change the speaker",
    body: (
      <>
        <Box component="span" sx={{ display: "block" }}>
          Click the <strong>Speaker</strong> icon to assign a sentence to a
          different speaker.
        </Box>
        <Box component="span" sx={{ display: "block", mt: 1 }}>
          To change the speaker for an entire transcript entry, select the
          speaker name at the top of that entry.
        </Box>
      </>
    ),
    target: "entry",
    reveal: "sentence-actions",
  },
  {
    icon: <WavingHand />,
    title: "Save your changes",
    body: (
      <>
        <Box component="span" sx={{ display: "block" }}>
          Before your changes are applied, you’ll have a chance to review them.
        </Box>
        <Box component="span" sx={{ display: "block", mt: 1 }}>
          Each save creates a new version, so previous versions can always be
          reviewed later.
        </Box>
      </>
    ),
    target: "history",
    reveal: "history-dialog",
  },
];

const HIGHLIGHT_PADDING = 8;

/**
 * Clamps a rect to the viewport so the spotlight ring and the anchored card stay
 * on-screen even when the target is taller/wider than the viewport.
 */
const clampToViewport = (r: DOMRect): DOMRect => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const top = Math.max(0, Math.min(r.top, vh));
  const left = Math.max(0, Math.min(r.left, vw));
  const bottom = Math.max(0, Math.min(r.bottom, vh));
  const right = Math.max(0, Math.min(r.right, vw));
  return new DOMRect(left, top, right - left, bottom - top);
};

/**
 * First-run walkthrough of the transcript editor. Spotlights the real on-screen
 * control for each step (elements tagged with `data-tour="..."`) and anchors an
 * explanatory card to it; steps without a target show a centered card. Auto-shows
 * once (tracked in localStorage by the page) and reopens via the Help button.
 */
export const TranscriptTutorialDialog: React.FC<TranscriptTutorialDialogProps> = ({
  open,
  onClose,
  onRevealChange,
}) => {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  // Rect of the dialog a dialog-step opens (speakers/history), so the card can
  // sit snug to its right instead of flush against the viewport edge.
  const [dialogRect, setDialogRect] = useState<DOMRect | null>(null);
  // The open dialog's `.MuiDialog-container` — the root of the dialog's own
  // focus trap. We portal the card into it (rather than <body>) because MUI
  // marks the modal's siblings `inert` while it's open, and its focus trap
  // yanks focus from anything outside the container — inside it, the card is
  // both clickable and keyboard-reachable.
  const [dialogRoot, setDialogRoot] = useState<Element | null>(null);

  // Restart at the beginning each time the tour is opened.
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  // Remember what had focus before the tour opened and hand it back on close.
  // Layout effect so we capture it before the focus trap moves focus into the
  // card (trap effects are passive and run later).
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    restoreFocusRef.current = previous instanceof HTMLElement ? previous : null;
    return () => {
      restoreFocusRef.current?.focus();
      restoreFocusRef.current = null;
    };
  }, [open]);

  // Escape dismisses the tour from anywhere within it. Capture phase so the
  // key still reaches us on dialog steps, where the revealed MUI dialog stops
  // Escape's propagation at its modal root; stopPropagation keeps that dialog
  // (and anything else) from also acting on the same keypress.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // An open popup (Select/Menu/Autocomplete) inside a revealed dialog
      // owns its own Escape — swallowing it here would close the tour and
      // strand the popup open.
      if (
        document.querySelector(".MuiPopover-root, .MuiAutocomplete-popper")
      ) {
        return;
      }
      event.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose]);

  const current = STEPS[step];
  const targetSelector = current.target
    ? `[data-tour="${current.target}"]`
    : null;

  // Tell the page what the current step wants revealed (dialogs, time-controls
  // preview); clear it when the tour closes/unmounts.
  useEffect(() => {
    onRevealChange?.(open ? current.reveal : undefined);
  }, [open, current.reveal, onRevealChange]);
  useEffect(() => () => onRevealChange?.(undefined), [onRevealChange]);

  const measure = useCallback(() => {
    if (!targetSelector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(targetSelector);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) {
      setRect(null);
      return;
    }
    setRect(clampToViewport(r));
  }, [targetSelector]);

  // Scroll the current target into view, measure it, and keep the spotlight
  // aligned while the page scrolls/resizes.
  useLayoutEffect(() => {
    if (!open) return;
    const el = targetSelector
      ? document.querySelector(targetSelector)
      : null;
    if (el) {
      el.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "center",
        inline: "center",
      });
    }
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    // Re-measure when the target resizes (e.g. the time-controls panel appears).
    let ro: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [open, step, targetSelector, measure, prefersReducedMotion]);

  // While a dialog step is active, keep the open dialog's rect current (it
  // animates in and re-centers on resize) so the card can anchor beside it.
  useLayoutEffect(() => {
    const isDialogStep =
      current.reveal === "speakers-dialog" ||
      current.reveal === "history-dialog";
    if (!open || !isDialogStep) {
      setDialogRect(null);
      setDialogRoot(null);
      return;
    }
    let raf = 0;
    const tick = () => {
      const paper = document.querySelector(
        ".MuiDialog-container .MuiPaper-root",
      );
      const root = paper ? paper.closest(".MuiDialog-container") : null;
      setDialogRoot((prev) => (prev === root ? prev : root));
      const next = paper ? paper.getBoundingClientRect() : null;
      setDialogRect((prev) => {
        if (!next) return prev === null ? prev : null;
        if (
          prev &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.height === next.height
        ) {
          return prev;
        }
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, step, current.reveal]);

  if (!open) return null;

  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  // Steps that open a real dialog (speakers/history). The card is portaled
  // inside that dialog's focus-trap root, so our own trap must not enforce
  // containment there — the two traps would fight over focus. The dialog's
  // trap already keeps focus inside the modal (which includes the card).
  const isDialogStep =
    current.reveal === "speakers-dialog" || current.reveal === "history-dialog";

  const card = (
    <Paper
      elevation={8}
      role="dialog"
      aria-modal="true"
      aria-label={`Transcript editor tutorial, step ${step + 1} of ${
        STEPS.length
      }: ${current.title}`}
      tabIndex={-1}
      sx={{
        width: 340,
        maxWidth: "calc(100vw - 32px)",
        p: 2.5,
        borderRadius: 3,
        outline: "none",
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          "& svg": { fontSize: 26 },
        }}
      >
        {current.icon}
      </Box>
      <Typography variant="subtitle1" fontWeight={700} mb={0.75}>
        {current.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {current.body}
      </Typography>

      <Box display="flex" gap={0.75} mt={2} mb={1.5}>
        {STEPS.map((_, i) => (
          <Box
            key={i}
            component="button"
            type="button"
            onClick={() => setStep(i)}
            aria-label={`Go to step ${i + 1}`}
            aria-current={i === step ? "step" : undefined}
            sx={{
              width: i === step ? 20 : 8,
              height: 8,
              borderRadius: 4,
              border: 0,
              p: 0,
              cursor: "pointer",
              transition: "width 0.2s ease, background-color 0.2s ease",
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
              },
              backgroundColor:
                i === step
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.primary, 0.2),
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          />
        ))}
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Button
          onClick={onClose}
          size="small"
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Skip
        </Button>
        <Box display="flex" gap={1}>
          {!isFirst && (
            <Button
              onClick={() => setStep((s) => s - 1)}
              size="small"
              startIcon={<ArrowBack fontSize="small" />}
              sx={{ textTransform: "none" }}
            >
              Back
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={() => (isLast ? onClose() : setStep((s) => s + 1))}
            endIcon={!isLast ? <ArrowForward fontSize="small" /> : undefined}
            sx={{ textTransform: "none" }}
          >
            {isLast ? "Got it" : "Next"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  // Re-mount + fade the card on each step change (keyed by `step`). The spotlight
  // highlight and any revealed dialog apply on render, while the card content eases
  // in with a small delay — so the surrounding visuals lead instead of the text
  // snapping to the next step ahead of them.
  //
  // The FocusTrap remounts with the card: each step moves focus onto the card
  // (whose role/aria-label announce the step) and, on non-dialog steps, keeps
  // Tab cycling within it. Restoring focus on close is handled by the layout
  // effect above, so MUI's own restore is disabled.
  const animatedCard = (
    <FocusTrap
      key={step}
      open
      disableEnforceFocus={isDialogStep}
      disableRestoreFocus
    >
      <Fade
        in
        appear
        timeout={prefersReducedMotion ? 0 : { enter: 1200, exit: 0 }}
        style={{ transitionDelay: prefersReducedMotion ? "0ms" : "80ms" }}
      >
        {card}
      </Fade>
    </FocusTrap>
  );

  // Steps that open a real dialog (speakers/history): the dialog is open behind
  // the card, and we lift + ring the trigger button above the dialog's backdrop
  // so it stays highlighted (the box-shadow spotlight can't cut through a modal).
  const anchorEl = rect
    ? { getBoundingClientRect: () => rect, nodeType: 1 }
    : null;

  // Place the dialog-step card to the right of the open dialog when it fits;
  // otherwise fall back to flush-right below the trigger button.
  const CARD_WIDTH = 340;
  const GAP = 16;
  const EDGE = 16;
  let dialogCardPosition: Record<string, string | number>;
  if (
    dialogRect &&
    dialogRect.right + GAP + CARD_WIDTH <= window.innerWidth - EDGE
  ) {
    dialogCardPosition = {
      top: Math.max(EDGE, dialogRect.top),
      left: dialogRect.right + GAP,
    };
  } else {
    dialogCardPosition = {
      top: rect ? rect.bottom + 16 : 88,
      right: EDGE,
      maxWidth: "calc(100vw - 32px)",
    };
  }

  if (isDialogStep) {
    return (
      // Portal into the open dialog's container (its focus-trap root) so the
      // card isn't a sibling MUI marks `inert` (which would block clicks) and
      // so the dialog's focus trap treats the card as inside the modal instead
      // of yanking focus away from it. Falls back to <body> until the dialog
      // has mounted.
      <Portal container={dialogRoot ?? undefined}>
        {targetSelector && (
          <GlobalStyles
            styles={{
              [targetSelector]: {
                position: "relative",
                zIndex: `${theme.zIndex.modal + 5} !important`,
                borderRadius: "8px",
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}, 0 0 0 8px ${alpha(
                  theme.palette.primary.main,
                  0.3,
                )}`,
              },
            }}
          />
        )}
        {/* Sit the card snug to the right of the centered dialog it opened, with
            a small gap. Anchoring to the dialog (not the trigger button) keeps it
            visually paired with what it describes and works regardless of dialog
            width. If there isn't room to its right (narrow viewport), fall back to
            flush-right below the trigger button.

            Only render once the dialog has been measured — otherwise the card
            would briefly flash in the corner (fallback position) before the
            dialog finishes opening. */}
        {dialogRect && (
          <Box
            sx={{
              ...dialogCardPosition,
              position: "fixed",
              zIndex: theme.zIndex.modal + 10,
              pointerEvents: "auto",
            }}
          >
            {animatedCard}
          </Box>
        )}
      </Portal>
    );
  }

  return (
    <Portal>
      {/* Force-reveal the hover-only control this step is about, so it's visible
          under the spotlight instead of hidden until hovered. */}
      {current.reveal === "sentence-actions" && (
        <GlobalStyles
          styles={{
            '[data-tour="entry"] .sentence-action': { opacity: "1 !important" },
          }}
        />
      )}
      {current.reveal === "add-divider" && (
        <GlobalStyles
          styles={{
            '[data-tour="add-divider"]': { opacity: "1 !important" },
          }}
        />
      )}
      {/* Click blocker + dim. With a target, the dim is the spotlight ring's
          giant box-shadow; without one, this layer dims the whole screen. */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: theme.zIndex.modal,
          backgroundColor: rect ? "transparent" : alpha("#000", 0.55),
        }}
      />
      {rect && (
        // Fade the spotlight ring (and its box-shadow dim) in on the same timing
        // as the card, so the highlight doesn't pop in ahead of the tutorial text.
        <Fade
          key={step}
          in
          appear
          timeout={prefersReducedMotion ? 0 : { enter: 1200, exit: 0 }}
          style={{ transitionDelay: prefersReducedMotion ? "0ms" : "80ms" }}
        >
          <Box
            sx={{
              position: "fixed",
              top: rect.top - HIGHLIGHT_PADDING,
              left: rect.left - HIGHLIGHT_PADDING,
              width: rect.width + HIGHLIGHT_PADDING * 2,
              height: rect.height + HIGHLIGHT_PADDING * 2,
              borderRadius: 2,
              border: `2px solid ${theme.palette.primary.main}`,
              boxShadow: `0 0 0 9999px ${alpha("#000", 0.55)}`,
              pointerEvents: "none",
              zIndex: theme.zIndex.modal + 1,
            }}
          />
        </Fade>
      )}

      {anchorEl ? (
        <Popper
          open
          anchorEl={anchorEl}
          // Anchor the card to the SIDE of the spotlighted element rather than
          // above/below it. A tall target (e.g. the video + time-controls panel)
          // would otherwise push a top/bottom card off-screen on short viewports,
          // leaving the card unreadable. `preventOverflow` with `altAxis` shifts
          // the card vertically so it always stays fully in view.
          placement="right-start"
          sx={{ zIndex: theme.zIndex.modal + 2 }}
          modifiers={[
            { name: "offset", options: { offset: [0, 16] } },
            {
              name: "flip",
              options: {
                padding: 12,
                fallbackPlacements: ["left-start", "bottom", "top"],
              },
            },
            { name: "preventOverflow", options: { padding: 12, altAxis: true } },
          ]}
        >
          {animatedCard}
        </Popper>
      ) : (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: theme.zIndex.modal + 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Box sx={{ pointerEvents: "auto" }}>{animatedCard}</Box>
        </Box>
      )}
    </Portal>
  );
};
